import { useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { BUNDLE_CYCLE } from "../buildInfo";

const API = process.env.REACT_APP_BACKEND_URL;
const CHECK_INTERVAL = 2 * 60 * 1000; // 2 minutes

// c61_206/207 — reload() alone re-serves the CACHED index.html
// (browser heuristic cache / CDN edge), so the tab boots the OLD
// bundle again. Nuke SW + Cache Storage, then force a cache MISS all
// the way through the CDN with a unique query string.
const nukeAndReload = async (stamp) => {
  try {
    if ("caches" in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  } catch { /* ignore */ }
  try {
    if ("serviceWorker" in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
  } catch { /* ignore */ }
  const u = new URL(window.location.href);
  u.searchParams.set("v", stamp);
  window.location.replace(u.toString());
};

export function useVersionCheck() {
  const knownVersion = useRef(null);
  const staleWarned = useRef(false);

  useEffect(() => {
    const check = async () => {
      try {
        const res = await axios.get(`${API}/api/system/version`, { headers: { "Cache-Control": "no-cache" } });
        const serverVersion = res.data?.version;
        const serverCycle = res.data?.cycle_marker;
        const flagsByCycle = res.data?.flags_by_cycle || null;
        if (!serverVersion) return;

        // c61_207 — STALE-BUNDLE detector. The old check only caught a
        // version CHANGE while the tab was open; a tab that BOOTS on an
        // old cached bundle looked perfectly healthy forever (the prod
        // "5s bulk timeout deployed but spinner still hangs" case).
        // The bundle now carries its own cycle marker: if the server's
        // registry KNOWS our cycle but its current cycle is different,
        // this bundle is provably older — self-heal once, then warn.
        const bundleIsStale =
          serverCycle && serverCycle !== BUNDLE_CYCLE
          && flagsByCycle
          && Object.prototype.hasOwnProperty.call(flagsByCycle, BUNDLE_CYCLE);
        if (bundleIsStale) {
          const guard = `snagger_stale_reload_${serverVersion}_${serverCycle}`;
          if (!sessionStorage.getItem(guard)) {
            sessionStorage.setItem(guard, String(Date.now()));
            console.warn(
              `[VersionCheck] STALE BUNDLE: browser runs ${BUNDLE_CYCLE}, `
              + `server is on ${serverCycle} — cache-busting reload`,
            );
            await nukeAndReload(`${serverVersion}-${Date.now()}`);
            return;
          }
          if (!staleWarned.current) {
            staleWarned.current = true;
            toast.error(
              `This browser is STILL running an old build (${BUNDLE_CYCLE}) `
              + `after an automatic refresh — the server is on ${serverCycle}. `
              + "Use OMS Settings → Deploy Verification → Force refresh.",
              { duration: 30000 },
            );
          }
        }

        if (knownVersion.current === null) {
          knownVersion.current = serverVersion;
          return;
        }

        if (serverVersion !== knownVersion.current) {
          console.log(`[VersionCheck] New deploy: ${knownVersion.current} → ${serverVersion}`);
          await nukeAndReload(serverVersion);
        }
      } catch {
        // Silent
      }
    };

    // Check on load
    check();

    // Check on interval
    const interval = setInterval(check, CHECK_INTERVAL);

    // Check when user returns to the tab
    const onFocus = () => check();
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") check();
    });

    return () => {
      clearInterval(interval);
      window.removeEventListener("focus", onFocus);
    };
  }, []);
}
